import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface ImportResult {
  SNo?: string;
  sNo?: string;
  message: string;
}

interface ImportResultsModalProps {
  results: ImportResult[];
  onClose: () => void;
}

const ImportResultsModal: React.FC<ImportResultsModalProps> = ({ results, onClose }) => {
  const [filterType, setFilterType] = useState<string>('all');

  // Get distinct values from the results for filtering
  const distinctValues = useMemo(() => {
    const values = new Set<string>();
    results.forEach(result => {
      if (result.message) {
        values.add(result.message);
      }
    });
    return Array.from(values);
  }, [results]);

  // Filter results based on selected filter
  const filteredResults = useMemo(() => {
    if (filterType === 'all') return results;
    return results.filter(result => result.message === filterType);
  }, [results, filterType]);

  // Get count for selected filter
  const getCountByFilter = (filter: string) => {
    if (filter === 'all') return results.length;
    return results.filter(result => result.message === filter).length;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-4xl max-h-[90vh]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Import Results</CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Filter by:</span>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ({results.length})</SelectItem>
                    {distinctValues.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value.length > 50 ? `${value.substring(0, 50)}...` : value} ({getCountByFilter(value)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={onClose} variant="outline">Close</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="min-w-full border">
              <thead>
                <tr>
                  <th className="border px-4 py-2">SNo</th>
                  <th className="border px-4 py-2">Message</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="border px-4 py-2 text-center text-gray-500">
                      No results found for the selected filter
                    </td>
                  </tr>
                ) : (
                  filteredResults.map((row: ImportResult, idx: number) => (
                    <tr key={idx}>
                      <td className="border px-4 py-2">{row.SNo || row.sNo || (idx + 2)}</td>
                      <td className="border px-4 py-2">{row.message}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Summary */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Showing {filteredResults.length} of {results.length} results
              </span>
              <div className="text-muted-foreground">
                {distinctValues.length} unique message types
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportResultsModal; 